import { z } from 'zod';
import { PropertyCategorySchema, PropertySubtypeSchema } from '../common/property-taxonomy.schema';
import { ConditionSchema, EnergyClassSchema } from '../property/properties.schemas';

/**
 * Public reference feed (IMM-408.2). The whitelist-only, publication-safe
 * projection served by `GET /public/v1/workspaces/:workspaceSlug/references`.
 *
 * This is an EXPLICIT projection: a new property field never widens the feed.
 * Only the fields below are ever emitted; street, house number, district, price,
 * owner/buyer/deal/activities, `miscellaneous`, `reference_number` and exact
 * coordinates are absent by construction (they are simply not in this schema).
 *
 * Field names and enum tokens are English (ADR 0009 — no German in the backend
 * or on the wire): `category` = the shared property category token, `subtype` =
 * the controlled subtype token, `condition`/`energyClass` = the property
 * condition / energy-class tokens. The homepage owns the display copy.
 */
export const ReferenceFeedImageSchema = z.object({
  url: z.string(),
  thumbnailUrl: z.string(),
  isCover: z.boolean(),
  position: z.number(),
});
export type ReferenceFeedImage = z.infer<typeof ReferenceFeedImageSchema>;

export const ReferenceFeedItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: PropertyCategorySchema,
  subtype: PropertySubtypeSchema.optional(),
  soldAt: z.string(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  livingArea: z.number().optional(),
  plotArea: z.number().optional(),
  rooms: z.number().optional(),
  constructionYear: z.number().optional(),
  condition: ConditionSchema.optional(),
  energyClass: EnergyClassSchema.optional(),
  description: z.string().optional(),
  locationDescription: z.string().optional(),
  features: z.string().optional(),
  location: z.object({ id: z.string(), name: z.string() }).optional(),
  images: z.array(ReferenceFeedImageSchema),
});
export type ReferenceFeedItem = z.infer<typeof ReferenceFeedItemSchema>;

export const ReferenceFeedResponseSchema = z.object({
  total: z.number(),
  items: z.array(ReferenceFeedItemSchema),
});
export type ReferenceFeedResponse = z.infer<typeof ReferenceFeedResponseSchema>;
